import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { onboardingAPI } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Upload, CheckCircle, Clock, AlertTriangle, FileText, 
  Shield, Calendar, ArrowLeft, Loader2, X, Pen, Edit3
} from 'lucide-react';
import { W9Form, DirectDepositForm, I9Form } from '../components/OnboardingForms';

export default function WorkerOnboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [signingWaiver, setSigningWaiver] = useState(null);
  const [signatureData, setSignatureData] = useState('');
  const [activeForm, setActiveForm] = useState(null); // 'w9_form', 'direct_deposit', 'i9_form'
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    fetchOnboardingStatus();
     
  }, []); // Intentionally run only on mount

  const fetchOnboardingStatus = async () => {
    try {
      setLoading(true);
      const response = await onboardingAPI.getWorkerStatus();
      setOnboarding(response.data);
    } catch (error) {
      toast.error('Failed to load onboarding status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (docType, file, expirationDate) => {
    try {
      setUploadingDoc(docType);
      const formData = new FormData();
      formData.append('document_type', docType);
      formData.append('file', file);
      if (expirationDate) {
        formData.append('expiration_date', expirationDate);
      }
      
      await onboardingAPI.uploadDocument(formData);
      toast.success('Document uploaded successfully');
      fetchOnboardingStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleFormSubmit = async (formType, formData) => {
    try {
      await onboardingAPI.submitForm(formType, formData);
      toast.success(`${formType.replace('_', ' ').toUpperCase()} submitted successfully`);
      setActiveForm(null);
      fetchOnboardingStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    }
  };

  const isFormType = (docType) => ['w9_form', 'direct_deposit', 'i9_form'].includes(docType);

  const handleSignWaiver = async (waiverType) => {
    if (!signatureData) {
      toast.error('Please provide your signature');
      return;
    }
    
    try {
      await onboardingAPI.signWaiver({
        waiver_type: waiverType,
        signature_data: signatureData
      });
      toast.success('Waiver signed successfully');
      setSigningWaiver(null);
      setSignatureData('');
      fetchOnboardingStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign waiver');
    }
  };

  // Signature canvas functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#BE4B6A';
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      const canvas = canvasRef.current;
      setSignatureData(canvas.toDataURL());
      setIsDrawing(false);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  if (!onboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Onboarding Not Found</h2>
            <p className="text-[var(--text-muted)]">Please contact support if you believe this is an error.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isBlocked = onboarding.onboarding_status === 'blocked';
  const isCompleted = onboarding.onboarding_status === 'completed' || onboarding.onboarding_status === 'approved';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--background)] to-[var(--background-subtle)] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button variant="ghost" onClick={() => navigate('/worker')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        {/* Status Banner */}
        <Card className={`mb-8 ${isBlocked ? 'border-red-500 bg-[var(--error)]/10' : isCompleted ? 'border-[var(--success)] bg-[var(--success)]/10' : ''}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
                  {isBlocked ? (
                    <><AlertTriangle className="w-6 h-6 text-[var(--error)]" /> Onboarding Blocked</>
                  ) : isCompleted ? (
                    <><CheckCircle className="w-6 h-6 text-[var(--success)]" /> Onboarding Complete</>
                  ) : (
                    <><Clock className="w-6 h-6 text-[var(--primary)]" /> Complete Your Onboarding</>
                  )}
                </h1>
                <p className="text-[var(--text-muted)] mt-1">
                  {isBlocked 
                    ? 'Your account is blocked. Please complete all requirements and contact support.'
                    : isCompleted
                    ? 'All requirements completed! You can now start working.'
                    : `You have ${onboarding.days_remaining} days remaining to complete all requirements.`
                  }
                </p>
              </div>
              
              <div className="text-right">
                <div className="text-3xl font-bold text-[var(--primary)]">{onboarding.progress_percent}%</div>
                <div className="text-sm text-[var(--text-muted)]">Complete</div>
              </div>
            </div>
            
            <Progress value={onboarding.progress_percent} className="mt-4 h-3" />
            
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-[var(--background-paper)]/50 rounded-lg p-3 text-center">
                <div className="font-bold">{onboarding.documents_completed}/{onboarding.documents_total}</div>
                <div className="text-sm text-[var(--text-muted)]">Documents</div>
              </div>
              <div className="bg-[var(--background-paper)]/50 rounded-lg p-3 text-center">
                <div className="font-bold">{onboarding.waivers_completed}/{onboarding.waivers_total}</div>
                <div className="text-sm text-[var(--text-muted)]">Waivers</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Required Documents
            </CardTitle>
            <CardDescription>Upload documents or complete forms as required</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {onboarding.documents?.filter(doc => doc.type !== 'companion_registry_check').map((doc) => (
                <div 
                  key={doc.type}
                  className={`p-4 rounded-xl border-2 ${
                    doc.status === 'uploaded' || doc.status === 'approved'
                      ? 'border-[var(--success)]/30 bg-[var(--success)]/10'
                      : isFormType(doc.type)
                      ? 'border-[#D4AF37]/30 bg-[#D4AF37]/10'
                      : 'border-dashed border-[var(--border-primary)] hover:border-[var(--primary)]'
                  } transition-all`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      {doc.status === 'uploaded' || doc.status === 'approved' ? (
                        <CheckCircle className="w-6 h-6 text-[var(--success)]" />
                      ) : isFormType(doc.type) ? (
                        <Edit3 className="w-6 h-6 text-[#D4AF37]" />
                      ) : (
                        <Upload className="w-6 h-6 text-gray-400" />
                      )}
                      <div>
                        <h3 className="font-semibold">{doc.name}</h3>
                        {isFormType(doc.type) && doc.status !== 'uploaded' && doc.status !== 'approved' && (
                          <p className="text-sm text-[#D4AF37]">Fill out form online</p>
                        )}
                        {doc.status === 'uploaded' && doc.uploaded_at && (
                          <p className="text-sm text-[var(--success)]">
                            {doc.form_data ? 'Completed' : 'Uploaded'} {new Date(doc.uploaded_at).toLocaleDateString()}
                          </p>
                        )}
                        {doc.expiration_date && (
                          <p className="text-sm text-[var(--text-muted)]">
                            Expires: {new Date(doc.expiration_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {doc.status !== 'uploaded' && doc.status !== 'approved' && (
                      <div className="flex gap-2">
                        {/* Fillable form button for W-9, Direct Deposit, I-9 */}
                        {isFormType(doc.type) ? (
                          <Button
                            size="sm"
                            className="bg-[#D4AF37]/100 hover:bg-[#E6C965] text-white"
                            onClick={() => setActiveForm(doc.type)}
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            Fill Form
                          </Button>
                        ) : (
                          <>
                            <input
                              type="file"
                              id={`file-${doc.type}`}
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  const expDate = prompt('Enter expiration date (YYYY-MM-DD) or leave empty:');
                                  handleFileUpload(doc.type, e.target.files[0], expDate || null);
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={uploadingDoc === doc.type}
                              onClick={() => document.getElementById(`file-${doc.type}`).click()}
                            >
                              {uploadingDoc === doc.type ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-1" />
                                  Upload
                                </>
                              )}
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Waivers Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Required Waivers & Agreements
            </CardTitle>
            <CardDescription>Read and sign all required agreements</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {onboarding.waivers?.map((waiver) => (
                <div 
                  key={waiver.type}
                  className={`p-4 rounded-xl border-2 ${
                    waiver.status === 'signed'
                      ? 'border-[var(--success)]/30 bg-[var(--success)]/10'
                      : 'border-[var(--border-primary)] hover:border-[var(--primary)]'
                  } transition-all`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {waiver.status === 'signed' ? (
                        <CheckCircle className="w-6 h-6 text-[var(--success)]" />
                      ) : (
                        <Pen className="w-6 h-6 text-gray-400" />
                      )}
                      <div>
                        <h3 className="font-semibold">{waiver.name}</h3>
                        <p className="text-sm text-[var(--text-muted)]">{waiver.description}</p>
                        {waiver.signed_at && (
                          <p className="text-sm text-[var(--success)] mt-1">
                            Signed {new Date(waiver.signed_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {waiver.status !== 'signed' && (
                      <Button
                        size="sm"
                        onClick={() => setSigningWaiver(waiver)}
                      >
                        <Pen className="w-4 h-4 mr-1" />
                        Sign
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Signature Modal */}
        {signingWaiver && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[var(--background-paper)] rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold">{signingWaiver.name}</h2>
                  <Button variant="ghost" size="sm" onClick={() => setSigningWaiver(null)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                
                <div className="bg-[var(--background-subtle)] p-4 rounded-lg mb-4 max-h-48 overflow-y-auto">
                  <p className="text-sm">{signingWaiver.description}</p>
                  <p className="text-sm mt-4 text-[var(--text-muted)]">
                    By signing below, I acknowledge that I have read, understood, and agree to the terms of this agreement.
                  </p>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Your Signature</label>
                  <div className="border-2 border-dashed border-[var(--border-primary)] rounded-lg p-2">
                    <canvas
                      ref={canvasRef}
                      width={400}
                      height={150}
                      className="w-full bg-[var(--background-paper)] cursor-crosshair touch-none"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearSignature} className="mt-2">
                    Clear Signature
                  </Button>
                </div>
                
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setSigningWaiver(null)} className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => handleSignWaiver(signingWaiver.type)}
                    className="flex-1 bg-[var(--primary)] text-white"
                    disabled={!signatureData}
                  >
                    Sign Agreement
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fillable Form Modals */}
        {activeForm === 'w9_form' && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[var(--background-paper)] rounded-2xl max-w-2xl w-full p-6">
              <W9Form 
                onSubmit={(data) => handleFormSubmit('w9_form', data)}
                onCancel={() => setActiveForm(null)}
              />
            </div>
          </div>
        )}

        {activeForm === 'direct_deposit' && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[var(--background-paper)] rounded-2xl max-w-2xl w-full p-6">
              <DirectDepositForm 
                onSubmit={(data) => handleFormSubmit('direct_deposit', data)}
                onCancel={() => setActiveForm(null)}
              />
            </div>
          </div>
        )}

        {activeForm === 'i9_form' && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[var(--background-paper)] rounded-2xl max-w-2xl w-full p-6">
              <I9Form 
                onSubmit={(data) => handleFormSubmit('i9_form', data)}
                onCancel={() => setActiveForm(null)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
